import ScreenBanner from "../../../../../components/ScreenBanner"
import DisplayNotFindInfo from "../../../../../components/DisplayNotFindInfo";
import {
    Box,
    Container,
    Grid,
    Paper,
    Stack,
    Typography,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination,
} from "@mui/material";
import React, { useState, useRef, useCallback, useMemo } from "react";
import useSetParamsURL from "../../../../../lib/useSetParamsURL";
import { useQuery } from "react-query";
import { useModalDetalheCooperado } from "../ModalDetalheCooperadoProvider";
import PermissionGuard from "../../../../../components/PermissionGuard";
import axiosClient from "../../../../../lib/axios";
import { IDataDetalharPublico } from "../../../../../types/api/cadastros/detalhar-publico";
import SearchWithFilters from "../../../../../components/BasicComponents/SearchWithFilters";
import DialogFiltrosPublico from "../PreFiltrosListagem";
import useDebounce from "../../../../../lib/useDebounce";
import ChipList from "../../../../../components/BasicComponents/ChipList";
import { getChipFilterItems } from "../../../../../helpers/getChipFilterItems";

export default function Listagem({
    tipo_publico,
}: ListagemPublicosProps) {
    const { label, permission } = TIPO_PUBLICO_MAP[tipo_publico];
    const [palavraChave, setPalavraChave] = useState("");
    const [filtros, setFiltros] = useState<TPreFilters>({});
    const [openFiltros, setOpenFiltros] = useState(false);
    const [page, setPage] = useState<number>(0)
    const [rowsPerPage, setRowsPerPage] = useState<number>(10);
    const inputRef = useRef<HTMLInputElement>(null);
    const { openModal } = useModalDetalheCooperado();
    const { setParamsUrl } = useSetParamsURL()
    const deboucedSearchTerm = useDebounce(palavraChave, 1000);

    const handleChangePage = (_: any, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const filters = useMemo(() => ({
        nome_pessoa_pesquisar: deboucedSearchTerm,
        ...filtros
    }), [deboucedSearchTerm, filtros])

    const { data } = useQuery(
        ["pesquisar-publicos", page, rowsPerPage, tipo_publico, filters],
        async () => {
            const params = {
                page: page + 1,
                limit: rowsPerPage,
                listar_bloqueados: true,
                tipo_publico: tipo_publico,
                ...filters,
                ...Object.fromEntries(
                    Object.entries(filters).filter(([_, v]) => v !== undefined && v !== '')
                )
            };
            const res = await axiosClient.get("/pesquisar-publicos", { params });
            return res.data;
        },
        {
            keepPreviousData: true,
            refetchOnMount: true
        }
    );

    const handleSearch = useCallback(() => {
        setPage(0);
    }, []);


    const publicos = data?.data || [];

    const total = data?.total || 0;

    const chipItems = useMemo(() => {
        return getChipFilterItems(
            filtros,
            filtersChipLabels,
        );
    }, [filtros]);


    const handleDeleteFilter = (item: string) => {
        const newFiltros = { ...filtros };

        if (item.startsWith('Departamento:')) {
            delete newFiltros.departamento;
        } else if (item.startsWith('Setor:')) {
            delete newFiltros.setor;
        } else if (item.startsWith('Fabricante:')) {
            delete newFiltros.fabricante;
        } else {
            Object.entries(filtros).forEach(([key, value]) => {
                const label = filtersChipLabels[key as keyof typeof filtersChipLabels] || key;
                const displayValue = Array.isArray(value) ? value.join(', ') : value;
                const chipText = `${label}: ${displayValue}`;

                if (chipText === item) {
                    delete newFiltros[key as keyof TPreFilters];
                }
            });
        }

        setFiltros(newFiltros);
    }


    return (
        <>
            <ScreenBanner title="Cadastros" />

            <PermissionGuard permissionId={permission}>
                <Container sx={{ mt: 3 }}>
                    <Typography textAlign="center" variant="h5">Públicos</Typography>
                    <Grid spacing={2} mt={1}>
                        <Grid>
                            <Box component={Paper}>
                                <Paper elevation={2} sx={{ p: 2 }}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" pt={2}>
                                        <Typography variant="h6">Lista de {label}</Typography>
                                    </Box>
                                    <SearchWithFilters
                                        palavraChave={palavraChave}
                                        textFieldProps={{
                                            inputRef: inputRef,
                                            label: "Pesquisar",
                                            placeholder: "Pesquise por nome",
                                            size: "medium",
                                        }}
                                        pesquisar={handleSearch}
                                        setTermoPesquisa={(value) => {
                                            setPalavraChave(value);
                                            setPage(0);
                                        }}
                                        handleOpenFilters={() => setOpenFiltros(true)}
                                    />
                                    <ChipList
                                        items={chipItems}
                                        onDelete={handleDeleteFilter}
                                    />


                                    <DialogFiltrosPublico
                                        open={openFiltros}
                                        onClose={() => setOpenFiltros(false)}
                                        tipoPublico={[tipo_publico]}
                                        filtrosAtuais={filtros}
                                        onConfirm={(novosFiltros) => {
                                            setFiltros(novosFiltros);
                                            setPage(0);
                                        }}
                                    />

                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell><b>ID</b></TableCell>
                                                <TableCell><b>Tipo</b></TableCell>
                                                <TableCell><b>Código</b></TableCell>
                                                <TableCell><b>Nome</b></TableCell>
                                                <TableCell><b>Documento</b></TableCell>
                                                <TableCell><b>Telefone</b></TableCell>
                                                <TableCell><b>Email</b></TableCell>
                                                <TableCell><b>Situação financeira</b></TableCell>
                                            </TableRow>
                                        </TableHead>

                                        <TableBody>
                                            {publicos.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={6}>
                                                        <DisplayNotFindInfo label="Nenhum publico encontrado" />
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                publicos.map((publico: Publico) => (
                                                    <TableRow
                                                        key={publico.id}
                                                        hover
                                                    >
                                                        <TableCell sx={{
                                                            cursor: "pointer",
                                                            color: "primary.main",
                                                            textDecoration: "underline",
                                                            fontWeight: "bold",
                                                            fontSize: "1rem",
                                                        }} onClick={() => {
                                                            setParamsUrl(publico.id.toString(), "detalhe-cooperado")
                                                            openModal(publico.id.toString())
                                                        }}>{publico.id || "-"}</TableCell>
                                                        <TableCell>{publico.tipo_publico || "-"}</TableCell>
                                                        <TableCell>{publico.codigo_publico || "-"}</TableCell>
                                                        <TableCell>{publico.nome_pessoa || "-"}</TableCell>
                                                        <TableCell>{publico.documento_pessoa}</TableCell>
                                                        <TableCell>{publico.telefone || "-"}</TableCell>
                                                        <TableCell>{publico.email || "-"}</TableCell>
                                                        <TableCell>{publico.situacao_financeira || "-"}</TableCell>
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>

                                    <Stack direction="row" justifyContent="flex-end">
                                        <TablePagination
                                            component="div"
                                            count={total}
                                            page={page}
                                            onPageChange={handleChangePage}
                                            rowsPerPage={rowsPerPage}
                                            onRowsPerPageChange={handleChangeRowsPerPage}
                                            labelRowsPerPage="Linhas por página"
                                        />
                                    </Stack>
                                </Paper>
                            </Box>
                        </Grid>
                    </Grid>
                    <DialogFiltrosPublico
                        tipoPublico={[tipo_publico]}
                        open={openFiltros}
                        onClose={() => setOpenFiltros(false)}
                        onConfirm={(filtros) => {
                            setOpenFiltros(false);
                            setPage(0);
                            setFiltros(filtros);
                        }}
                        filtrosAtuais={filtros} />
                </Container>
            </PermissionGuard>
        </>
    )
}

const TIPO_PUBLICO_MAP = {
    'A': { label: 'cooperados', permission: 'cadastros.cooperado.listar-todos' },
    'C': { label: 'clientes', permission: 'cadastros.cliente.listar-todos' },
    'F': { label: 'fornecedores', permission: 'cadastros.fornecedor.listar-todos' },
    'E': { label: 'empregados', permission: 'cadastros.empregado.listar-todos' },
} as const;

export interface ListagemPublicosProps {
    tipo_publico: "A" | "C" | "F" | "E"
}

export type TPreFilters = {
    [key: string]: any
}

export type Publico = IDataDetalharPublico & {
    id: string,
}
const filtersChipLabels: { [key in keyof TPreFilters]: string } = {
    nome_pessoa_pesquisar: 'Nome',
    documento_pessoa_pesquisar: 'Documento',
    email_pesquisar: 'Email',
    codigo_publico_contem: 'Código',
};